import { FaBatteryEmpty, FaBatteryHalf, FaBatteryFull, FaArrowUp, FaArrowDown, FaSolarPanel, FaBolt } from 'react-icons/fa';
import { LucideMessageCircleQuestion, Sun, ArrowDownCircle, ArrowUpCircle, Zap, ZapOff } from 'lucide-react';
import { GiRegeneration, GiWindTurbine } from "react-icons/gi";
import { InverterAction, ModbusConfig, ModbusConfigItem, PowerMeter, SiteSpecs, User, ManualAction, Site } from '../types/global';
import { UserSessionRequestError } from './authRedirect';
import { siteZoneOptions } from '@/app/lib/siteDateTime';

// A clock time with the zone spelled out beside it. `timeZone` names the
// site's zone, so a viewer elsewhere reads the site's clock and can see which
// clock it is; omitted (or unusable) it falls back to the viewer's own.
export const formatTimeWithTZ = (date: Date, timeZone?: string | null) => {
    return date.toLocaleTimeString('en-AU', siteZoneOptions(timeZone, {
      timeZoneName: 'short'
    }));
  };

  export const timeSince = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) {
      return interval + " year" + (interval > 1 ? "s" : "") + " ago";
    }
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      return interval + " month" + (interval > 1 ? "s" : "") + " ago";
    }
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) {
      return interval + " day" + (interval > 1 ? "s" : "") + " ago";
    }
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) {
      return interval + " hour" + (interval > 1 ? "s" : "") + " ago";
    }
    interval = Math.floor(seconds / 60);
    if (interval >= 1) {
      return interval + " minute" + (interval > 1 ? "s" : "") + " ago";
    }
    return Math.floor(seconds) + " second" + (seconds > 1 ? "s" : "") + " ago";
  };

// Generate link to run simulation with this interval's data
export const getRulesLink = (action: InverterAction) => {
  return `/rules?inverter_action_id=${action.id}&inverter_id=${action.inverter_id}`;
};

export const guestMessage = (user: User | null) => (
  <div className="flex flex-col items-center justify-center w-full p-4">
    <h1 className="text-2xl font-bold mb-4">Welcome to the Powston Dashboard</h1>
    <p className="mb-2">
      Explore real, anonymized bills that show solar, battery, and usage data.
      Adjust the date range (e.g., to a quarter or an entire year) and filter by state 
      to focus on the most relevant information for your region.
    </p>
    <p className="mb-4">
      Use this to demonstrate potential returns on battery installations. 
      <a href="/rates" className="text-blue-700 hover:text-primary underline font-semibold">
        Visit the rates page
      </a>{' '}
      to see how our customers are saving.
    </p>
    {user && (user?.lv_nmi || user?.cb_nmi) ? (
      <div role="alert" className="mb-2 w-full p-3 border-l-4 border-yellow-500 bg-yellow-50 text-yellow-800 rounded">
        <div className="font-semibold">Warning: Utility meter detected but no connected site</div>
        <div className="text-sm">
          It appears you have a utility meter but no Powston-connected site. This can mean your battery isn't being tariff‑aware and you may be paying peak rates. Connect your site or contact support to enable controlled, cost‑optimised operation.
        </div>
        <div className="mt-2">
          <p className="text-sm">
            Please <a
              href="mailto:service@powston.com?subject=Connect%20my%20inverter&body=Hi%2C%0A%0ACan%20you%20please%20connect%20my%20inverter%3F%0A%0ABrand%3A%20%5Bbrand%5D%0ASerial%20number%3A%20%5Bserial%5D%0ASite%20ID%20or%20NMI%3A%20%5Bsite%20ID%5D%0A%0AThank%20you."
              className="font-semibold text-blue-700 hover:underline"
            >
              email us
            </a>{' '}
            or{' '}
            <strong>
              report a problem
            </strong>{' '}
            and include your inverter brand and serial number so we can connect it as soon as possible.
          </p>
        </div>
      </div>
    ) : null}
  </div>
);

export function actionColor(a: string) {
  switch (true) {
    case /^export/.test(a):
      return "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-700";
    case /^import/.test(a):
      return "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300 ring-sky-200 dark:ring-sky-700";
    case /^curtail/.test(a):
      return "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-700";
    case a === "auto":
      return "bg-slate-100 dark:bg-slate-800/40 text-slate-800 dark:text-slate-300 ring-slate-200 dark:ring-slate-700";
    case a === "stopped":
      return "bg-zinc-100 dark:bg-zinc-800/40 text-zinc-800 dark:text-zinc-300 ring-zinc-200 dark:ring-zinc-700";
    case /^charge/.test(a):
      return "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-700";
    case /^discharge/.test(a):
      return "bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 ring-purple-200 dark:ring-purple-700";
    default:
      return "bg-slate-100 dark:bg-slate-800/40 text-slate-800 dark:text-slate-300 ring-slate-200 dark:ring-slate-700";
  }
}

export function formatW(v?: number | null) {
  if (v === undefined || v === null || !Number.isFinite(v)) return "–";
  const abs = Math.abs(v);
  const sign = v >= 0 ? "" : "-";
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)} kW`;
  return `${sign}${abs.toFixed(0)} W`;
}

export const noneMessage = (
  <div className="flex flex-col items-center justify-center w-full p-4">
    <h1 className="text-2xl font-bold mb-4">No Inverters Found</h1>
    <p className="mb-2">
      It seems there are no inverters available for display. 
      Please check back later or contact support if you believe this is an error.
    </p>
  </div>
);

export const getBatteryActionPart = (action: string) => {
  const actionParts = typeof action === 'string' ? action.split('-') : [];
  const batteryAction = actionParts.length > 1 ? actionParts[0] : action;
  return batteryAction;
};

export const getIcon = (price: number) => {
  if (price < 0) return "⚠️";
  if (price < 0) return "❗";
  if (price > 0.60) return "🔥";
  return null;
};

export const getPriceColorClass = (price: number) => {
  if (price < 0) return 'bg-green-200 text-green-800 dark:text-amber-400';
  if (price < 0.25) return 'bg-green-200 text-green-800 dark:text-green-400';
  if (price < 0.35) return 'bg-amber-400 text-amber-800 dark:text-blue-400';
  return 'bg-red-200 text-red-800 dark:text-red-400';
};


export const getSellPriceColorClass = (price: number) => {
  if (price < 0) return 'bg-red-100 text-amber-800';
  if (price < 0.25) return 'bg-amber-100 text-amber-800';
  if (price < 0.35) return 'bg-amber-200 text-amber-800';
  return 'bg-blue-200 text-primary';
};

export const drawBattery = (soc: number) => {
    if (soc < 25){
      return <FaBatteryEmpty size={100} className="h-8 mb-2 text-red-500" />;
    } else if (soc < 55) {
      return <FaBatteryHalf size={100} className="h-8 mb-2 text-yellow-500" />;
    } else {
      return <FaBatteryFull size={100} className="h-8 mb-2 text-green-500" />;
    }
  }

export  const renderArrow = (value: number) => {
  if (value < 0) {
    return <FaArrowUp className="text-blue-500 text-4xl" />;
  } else {
    return <FaArrowDown className="text-green-500 text-4xl" />;
  }
};

export  const renderLowerArrow = (value: number) => {
  if (value < 0) {
    return <FaArrowDown className="text-blue-500 text-4xl" />;
  } else {
    return <FaArrowUp className="text-green-500 text-4xl" />;
  }
};

export const formatPower = (watts?: number | null): string => {
  // Missing telemetry must read as "no data", never "NaN W" or a fake 0.
  if (watts === undefined || watts === null || !Number.isFinite(watts)) {
    return '–';
  }
  if (Math.abs(watts) >= 1000000) {
    return `${(watts / 1000000).toFixed(2)} MW`;
  }
  if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`;
  }
  return `${Math.round(watts).toFixed(2)} W`;
};

export const logoUrl = (logo: string) => {
  if (logo && logo.startsWith('http')) {
    return logo;
  } else if (logo && logo.startsWith('/')) {
    return logo;
  } else if (logo) {
    return `/images/logos/${logo}`;
  } else {
    return '/images/logos/powston-dots.svg';
  }
}

export const properCase = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Non-finite input (a missing field, a stub row, 0/0) renders as an em dash
// rather than "$NaN" — an owner must never see NaN as a money figure.
export const MISSING_CURRENCY = '—';

export const numberToCurrency = (value: number, dollars = false, cents = false, decimalPlaces = 2) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return MISSING_CURRENCY;
  }
  if (dollars) {
    return `$${value.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } else if (cents) {
    return `${(value * 100).toFixed(decimalPlaces)}¢`;
  } else if (value >= 1) {
    return `$${value.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } else if (value < -1) {
    return `$${value.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } else {
    return `${(value * 100).toFixed(decimalPlaces)}¢`;
  }
};

export const describe_conditions = (item: ManualAction) => {
      const parts = [];
      if (item.min_sell_price && item.max_sell_price) {
          parts.push(`price sell between $${item.min_sell_price.toFixed(2)} - $${item.max_sell_price.toFixed(2)}/kWh`);
      }
      if (item.min_buy_price && item.max_buy_price) {
          parts.push(`price buy between $${item.min_buy_price.toFixed(2)} - $${item.max_buy_price.toFixed(2)}/kWh`);
      }
      if (item.min_soc && item.max_soc) {
          parts.push(`SOC between ${item.min_soc}% - ${item.max_soc}%`);
      }
      return parts.length > 0 ? `Only when ${parts.join(' and ')}` : '';
  };

export const parseDateAssumeUTC = (dateString: string): Date => {
  // If the string doesn't include "Z" or a timezone offset, add "Z" to assume UTC
  if (!dateString.includes("Z") && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
      return new Date(dateString + "Z");
  }
  return new Date(dateString);
}

// A meter is stale when it has never reported or its last reading is more
// than 10 minutes old. Used to gate display of power/state — those fields
// are zeroed / blank for never-reported meters, and reflect a frozen
// snapshot for silent ones.
export const POWER_METER_STALE_MS = 10 * 60 * 1000;
export const isPowerMeterStale = (meter: { time?: Date | string | null; has_recent_reading?: boolean }): boolean => {
  if (meter.has_recent_reading === false) return true;
  if (!meter.time) return true;
  const t = meter.time instanceof Date ? meter.time : new Date(meter.time);
  if (Number.isNaN(t.getTime())) return true;
  return Date.now() - t.getTime() > POWER_METER_STALE_MS;
};

export const fetchPowerMeters = (
  site_id: number,
  startDate: Date,
  endDate: Date,
  setPowerMeters: (data: PowerMeter[]) => void) => {
  const url = `/api/meter_datas/${site_id}?from=${startDate.toLocaleDateString('en-CA')}&to=${endDate.toLocaleDateString('en-CA')}`;

  fetch(url)
    .then(response => response.json())
    .then(data => {
      // The backend has occasionally returned `{detail: ...}` on auth /
      // permission errors which used to crash this `.map` call with
      // "data.map is not a function". Tolerate non-array responses by
      // coercing to []; surface an empty list rather than a hard error.
      const list: PowerMeter[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.power_meters) ? data.power_meters
        : Array.isArray(data?.meters) ? data.meters
        : [];
      // Convert each `time` field to a Date assuming UTC when missing tz.
      // Drop meters flagged hidden by the backend — every consumer expects a
      // user-facing list, so filtering here keeps the rule in one place. Use
      // strict `=== true` so older backend payloads without the field don't
      // accidentally hide everything.
      const parsedData = list
        .filter((meter: PowerMeter) => meter.hidden !== true)
        .map((meter: PowerMeter) => ({
          ...meter,
          time: parseDateAssumeUTC(meter.time as unknown as string),
        }));

      setPowerMeters(parsedData);
    })
    .catch(err => {
      console.error('Failed to fetch power meters:', err);
      setPowerMeters([]);
    });
}

export const showActionIcon = (action: string) => {
  if (!action) return <LucideMessageCircleQuestion className="text-gray-500" size={18} />;
  const actionLower = action.toLowerCase();
  if (actionLower.includes('import')) {
    return <ArrowDownCircle className="text-blue-600" size={18} />;
  } else if (actionLower.includes('export')) {
    return <ArrowUpCircle className="text-green-500" size={18} />;
  } else if (actionLower.includes('auto')) {
    return <Zap className="text-yellow-500" size={18} />;
  } else if (actionLower.includes('curtail')) {
    return <ZapOff className="text-gray-500" size={18} />;
  } else {
    return <Sun className="text-yellow-500" size={18} />;
  }
};

export function parseUtcTime(time: string | Date): Date {
  return new Date(time.toString() + "Z");
}

/**
 * Pick the most recent valid timestamp across several candidates and report
 * whether it's older than `thresholdMs`.
 *
 * Background: the inverter status response carries multiple freshness
 * signals — `last_inverter_time`, `cached_time`, and `interval_time`.
 * Historically `isOffline` checks only looked at `last_inverter_time`,
 * which can lag or be unset even when `cached_time` and `interval_time`
 * are seconds old, producing false "No data available: Offline" cards
 * for sites that are reporting just fine.
 *
 * Returns:
 *   { isStale: true,  age: number | null }  when no valid timestamp exists
 *                                            or the freshest is older than
 *                                            the threshold
 *   { isStale: false, age: number }          when the freshest is within
 *                                            the threshold
 */
export function isInverterStale(
  candidates: Array<Date | string | number | null | undefined>,
  thresholdMs: number,
  now: number = Date.now(),
): { isStale: boolean; age: number | null; freshest: Date | null } {
  let freshest: Date | null = null;
  for (const c of candidates) {
    if (c == null) continue;
    const d = c instanceof Date ? c : new Date(c);
    if (isNaN(d.getTime())) continue;
    if (!freshest || d.getTime() > freshest.getTime()) freshest = d;
  }
  if (!freshest) return { isStale: true, age: null, freshest: null };
  const age = now - freshest.getTime();
  return { isStale: age > thresholdMs, age, freshest };
}

// Settled (Act) rows: `costs` bundles the energy imported AND the daily supply
// charge spread across every 5-minute interval. Shown blended, a 3 Wh import
// reads as "cost 1¢" and owners go hunting for a load that isn't there, so the
// per-interval value is the ENERGY component; the supply charge is shown beside
// it (supplyChargeDollars) and the bill totals still include it.
// Always derived as costs − fixed_costs (never the stored energy_costs, which
// is on a different tax basis for some retailers) so the two displayed parts
// reconcile exactly with the billed total other callers show.
// Unknown or negative fee splits keep the billed value intact; every caption
// and calculation must agree on whether energy can be separated from fees.
export const hasSupplyChargeSplit = (action: InverterAction): action is InverterAction & { fixed_costs: number } =>
  typeof action.fixed_costs === 'number' && Number.isFinite(action.fixed_costs) && action.fixed_costs >= 0;

const settledEnergyCostCents = (action: InverterAction): number => {
  if (hasSupplyChargeSplit(action)) {
    return action.costs - action.fixed_costs;
  }
  return action.costs;
};

// Daily supply charge apportioned to this interval, in dollars (0 when the row
// is not settled or the backend did not send the split).
export const supplyChargeDollars = (action: InverterAction): number => {
  return action.quality === 'Act' && hasSupplyChargeSplit(action) ? action.fixed_costs / 100 : 0;
};

// Billed value of the interval in dollars (negative = earnings). For settled
// rows this is the full billed net (energy + supply charge − earnings), which
// is what totals and bill-style callers want. Per-interval cards that present
// the fee separately use estimateEnergyCost instead.
export const estimateCost = (action: InverterAction) => {
  if (action.quality == 'Act') {
    return (action.costs - action.earnings) / 100;
  }
  return estimateUnsettledCost(action);
};

// Energy-only value of the interval in dollars (negative = earnings): the
// settled supply charge is excluded so it can be shown beside the value with
// supplyChargeDollars. Identical to estimateCost for unsettled rows.
export const estimateEnergyCost = (action: InverterAction) => {
  if (action.quality == 'Act') {
    return (settledEnergyCostCents(action) - action.earnings) / 100;
  }
  return estimateUnsettledCost(action);
};

const estimateUnsettledCost = (action: InverterAction) => {
  if (!action) return 0;
  const { buyPrice, sellPrice } = intervalPrices(action);
  if (action.e_grid == 0) return 0;
  if (action.e_grid < 0) return -action.e_grid / 1000 * buyPrice / 100;
  if (action.e_grid > 0) return -action.e_grid / 1000 * sellPrice / 100;
  return 0;
};

export function computeEnergy(data: InverterAction) {
  let energyKWh = 0;
  if ((data.energy_export > 0) || (data.energy_import > 0)) {
    energyKWh = data.energy_export - data.energy_import;
  } else {
    // Calculate grid energy
    energyKWh = data.e_grid / 1000; // Convert Wh to kWh
  }
  return energyKWh;
}


export const getActionColor = (action: string) => {
  if (!action) return 'text-gray-500';
  const actionLower = action.toLowerCase();
  if (actionLower.includes('import')) return 'text-blue-600';
  if (actionLower.includes('export')) return 'text-green-500';
  if (actionLower.includes('auto')) return 'text-yellow-500';
  if (actionLower.includes('curtail')) return 'text-gray-500';
  return 'text-yellow-500';
};

// ── Interval clarity helpers ─────────────────────────────────────────────
// Single source of truth for how each 5-minute interval's
// direction, energy, price and value are worded — so the desktop table, the
// mobile card, tooltips and the plain-language explanation can never drift.

// Quality phrasing used everywhere we distinguish settled meter data from
// estimated inverter readings. quality === 'Act' is settled/billable.
export const qualityLabel = (action: InverterAction): string => {
  if (action.quality !== 'Act') return 'Estimated from inverter readings';
  return describeFlow(action).isMetered
    ? 'Settled meter data'
    : 'Settled financial value; estimated inverter flow';
};

// Apply the display threshold to EACH channel: two non-zero channels must
// never become Idle just because they cancel out.
const FLOW_EPSILON_KWH = 0.0005;

const intervalPrices = (action: InverterAction) => {
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const buyPrice = action.buy_price ?? parsed?.buy_price;
  return {
    buyPrice: buyPrice ?? 0,
    hasBuyPrice: typeof buyPrice === 'number' && Number.isFinite(buyPrice),
    sellPrice: action.sell_price ?? parsed?.sell_price ?? 0,
  };
};

export type IntervalFlowLeg = {
  word: 'Export' | 'Import';
  arrow: '↑' | '↓';
  energyKWh: number;
  kw: string;
  kwh: string;
  priceCents: number;
  colorClass: string;
};

// The shared display classification. Net energy remains useful for charts
// (computeEnergy), but cannot describe how much crossed the meter both ways.
// Settled zero channels are authoritative too; only absent/invalid gross data
// falls back to inverter net readings. Estimates never use gross channels.
export const describeFlow = (action: InverterAction): {
  word: 'Export' | 'Import' | 'Bidirectional' | 'Idle';
  legs: IntervalFlowLeg[];
  totalKWh: number;
  isMetered: boolean;
  idleText: string;
} => {
  const validChannel = (n: number) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
  const isMetered = action.quality === 'Act'
    && validChannel(action.energy_export) && validChannel(action.energy_import);
  const net = Number.isFinite(action.e_grid) ? action.e_grid / 1000 : 0;
  const exported = isMetered ? action.energy_export : Math.max(net, 0);
  const imported = isMetered ? action.energy_import : Math.max(-net, 0);
  const { buyPrice, sellPrice } = intervalPrices(action);
  const legs: IntervalFlowLeg[] = [];
  const addLeg = (word: IntervalFlowLeg['word'], energyKWh: number, priceCents: number) => {
    if (energyKWh < FLOW_EPSILON_KWH) return;
    const isExport = word === 'Export';
    legs.push({
      word, energyKWh, priceCents, arrow: isExport ? '↑' : '↓',
      kw: formatPower(energyKWh * 12000),
      kwh: `${energyKWh.toFixed(3)} kWh`,
      colorClass: isExport ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400',
    });
  };
  addLeg('Export', exported, sellPrice);
  addLeg('Import', imported, buyPrice);
  return {
    word: legs.length === 2 ? 'Bidirectional' : legs[0]?.word ?? 'Idle',
    legs,
    totalKWh: legs.reduce((sum, leg) => sum + leg.energyKWh, 0),
    isMetered,
    idleText: exported + imported > 0
      ? `${isMetered ? 'Grid' : 'Net grid'} flow below display precision this interval`
      : `No ${isMetered ? '' : 'net '}grid flow this interval`,
  };
};

// A presentation band, not a test of control success or telemetry health.
// Keep both gross flows prominent when charges cancel, and never mistake an
// absent tariff or an unknown supply-charge split for a free import.
export const isLowCostImport = (action: InverterAction): boolean => {
  const flow = describeFlow(action);
  if (flow.word !== 'Import') return false;
  if (action.quality === 'Act') {
    if (!flow.isMetered || action.energy_export !== 0 || action.earnings !== 0
      || !hasSupplyChargeSplit(action) || !Number.isFinite(action.costs)) return false;
  } else if (!intervalPrices(action).hasBuyPrice) {
    return false;
  }
  const cost = estimateEnergyCost(action);
  // Allow for subtraction noise at exactly 1¢; never round the displayed value
  // before deciding which side of the boundary it belongs to.
  return Number.isFinite(cost) && cost >= 0 && cost + Number.EPSILON < 0.01;
};

export const LOW_COST_IMPORT_CONTEXT =
  'Small imports can occur during normal grid-connected operation as the system responds to changing demand. Import alone does not show a fault. This amount covers five minutes; repeated imports contribute to your daily total.';

// Dollars with adaptive precision so a genuine sub-cent value never reads as
// "$0.00": >= 10¢ shows as dollars (2dp), below that as cents (1dp, "0.8¢").
export const formatMoneyAdaptive = (dollars: number): string => {
  const mag = Math.abs(dollars);
  return mag >= 0.10
    ? numberToCurrency(mag, true)
    : numberToCurrency(mag, false, true, 1);
};

// Value of an interval, worded (Earned / Cost / —) with adaptive precision so a
// genuine value never reads as "$0.00": values under 10¢ show as cents (e.g.
// "0.8¢"). Per-interval ONLY — never use for daily summary totals.
export const formatIntervalValue = (action: InverterAction): {
  word: 'Earned' | 'Cost' | '';
  text: string;
  amountText: string;
  colorClass: string;
} => {
  const v = estimateEnergyCost(action); // dollars; negative = profit/earnings
  const mag = Math.abs(v);
  const lowCost = isLowCostImport(action);
  if (mag === 0 && !lowCost) {
    return { word: '', text: '—', amountText: '—', colorClass: 'text-muted-foreground' };
  }
  const amountText = formatMoneyAdaptive(mag);
  const earned = v < 0;
  return {
    word: earned ? 'Earned' : 'Cost',
    text: `${earned ? 'Earned' : 'Cost'} ${amountText}`,
    amountText,
    colorClass: lowCost ? 'text-muted-foreground'
      : earned ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
  };
};

// Rates give context; multiplying them must not masquerade as settled charges
// because retailer tax/tariff adjustments can differ from the quoted rates.
export const intervalRateSummary = (action: InverterAction): string =>
  describeFlow(action).legs.map(leg =>
    `${leg.word} ${leg.kwh} at ${leg.priceCents.toLocaleString('en-AU', { maximumFractionDigits: 4 })}¢/kWh`
  ).join('; ');

// The money portion is also used in the plain-language panel, where repeating
// all the readings, rates and quality captions would bury the explanation.
export const intervalValueEquation = (action: InverterAction): string => {
  const value = formatIntervalValue(action);
  const result = value.word ? value.text : '0.0¢ net';
  if (action.quality !== 'Act') return `Estimated net value: ${result}`;
  const signedMoney = (cents: number) => `${cents < 0 ? '−' : ''}${formatMoneyAdaptive(cents / 100)}`;
  const splitKnown = hasSupplyChargeSplit(action);
  const costLabel = splitKnown ? 'import cost' : 'cost (including any unsplit fees)';
  return `Settlement: ${signedMoney(settledEnergyCostCents(action))} ${costLabel} − (${signedMoney(action.earnings)} export earnings) = ${result}`;
};

// Every value explanation uses the same channels and the same recorded money
// as the header. In particular equal gross flows can still have a net cost.
export const intervalEquation = (action: InverterAction): string => {
  const flow = describeFlow(action);
  const rates = flow.word === 'Idle' ? flow.idleText : intervalRateSummary(action);
  const money = intervalValueEquation(action);
  const supply = supplyChargeDollars(action);
  const supplySuffix = supply > 0
    ? ` + ${numberToCurrency(supply, false, true, 1)} daily supply charge (charged every interval, even with no import)`
    : '';
  if (action.quality === 'Act') {
    return `${rates}. ${money} (${qualityLabel(action)})${supplySuffix}`;
  }
  return `${rates}. ${money} (${qualityLabel(action)}; net flow only)`;
};

// ── Settled-data calendar reminder (client-side, no backend) ──────────────
// Builds an iCalendar VEVENT (+ alarm) reminding the user to check the final,
// settled figures once retailer meter data should have arrived. We set it a
// safe ~2 days after the interval (settlement typically lands by noon the next
// day). The reminder lives entirely in the user's own calendar — Powston
// stores nothing.
const icsStamp = (d: Date): string => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

const settlementReminderWindow = (action: InverterAction): { start: Date; end: Date; dateLabel: string } => {
  const interval = parseUtcTime(action.time as unknown as string);
  const start = new Date(interval);
  start.setDate(start.getDate() + 2);
  start.setHours(9, 0, 0, 0); // 9am local, two days on
  const end = new Date(start.getTime() + 30 * 60 * 1000);
  return { start, end, dateLabel: interval.toLocaleDateString() };
};

export const buildSettlementReminderIcs = (action: InverterAction): string => {
  const { start, end, dateLabel } = settlementReminderWindow(action);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Powston//Settlement Reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:powston-settlement-${action.id}@powston`,
    `DTSTAMP:${icsStamp(new Date())}`,
    `DTSTART:${icsStamp(start)}`,
    `DTEND:${icsStamp(end)}`,
    `SUMMARY:Check settled trading data for ${dateLabel}`,
    `DESCRIPTION:Utility meter data for your ${dateLabel} trading interval should now be settled. Open Powston to review the final, verified figures.${url ? '\\n' + url : ''}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:Powston settled-data reminder',
    'TRIGGER:PT0S',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

// Hand the .ics to the OS calendar via a Blob download (opens Apple/Google
// Calendar on import). No server round-trip.
export const downloadIcs = (filename: string, ics: string): void => {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// One-click convenience for Google Calendar users.
export const googleCalendarUrl = (action: InverterAction): string => {
  const { start, end, dateLabel } = settlementReminderWindow(action);
  const text = encodeURIComponent(`Check settled trading data for ${dateLabel}`);
  const details = encodeURIComponent(`Utility meter data for your ${dateLabel} trading interval should now be settled. Open Powston to review the final, verified figures.`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${icsStamp(start)}/${icsStamp(end)}&details=${details}`;
};

export const displayConfig = (rate: SiteSpecs) => {
  const inverter_power_kw = Math.round(rate.inverter_power/1000);
  const installed_solar_kw = Math.round(rate.installed_solar/1000);
  const installed_battery_kwh = Math.round(rate.installed_battery/1000);
  const installed_wind_kwh = Math.round((rate?.wind_power || 0)/1000);
  const installed_hydro_kwh = Math.round((rate?.hydro_power || 0)/1000);

  return (
    <div className="hidden lg:inline-flex lg:items-center lg:space-x-4">
    <div className="inline-flex items-center space-x-4">
      {installed_wind_kwh > 0 && <div className="flex items-center space-x-1">
        <GiWindTurbine className="text-green-800" title={`${installed_wind_kwh}kW`} />
        <span className="text-sm">{installed_wind_kwh}kW</span>
      </div>}
      {installed_hydro_kwh > 0 && <div className="flex items-center space-x-1">
        <GiRegeneration className="text-primary" title={`${installed_hydro_kwh}kW`} />
        <span className="text-sm">{installed_hydro_kwh}kW</span>
      </div>}
      <div className="flex items-center space-x-1">
        <FaSolarPanel className={installed_solar_kw > 0 ? 'text-amber-500' : 'text-gray-400'} title={`${installed_solar_kw}kW`} />
        <span className="text-sm">{installed_solar_kw}kW</span>
      </div>
      <div className="flex items-center space-x-1">
        <FaBolt className="text-green-800" title={`${inverter_power_kw}kW`} />
        <span className="text-sm">{inverter_power_kw}kW</span>
      </div>
      <div className="flex items-center space-x-1">
        <FaBatteryFull className={installed_battery_kwh > 0 ? 'text-cyan-800' : 'text-gray-400'} title={`${installed_battery_kwh}kWh`} />
        <span className="text-sm">{installed_battery_kwh}kWh</span>
      </div>
    </div>
    </div>
  )

}

export const formatValue = (value: any, unit: string, round_factor=1) => {
  if (value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value))) return 'N/A';
  let formattedValue = value;
  if (typeof value === 'number') {
    value = Math.round(value*(round_factor*10))/(round_factor*10);
    let cut_off = 1;
    if (round_factor > 10){
      cut_off = 3;
    }
    // No need to show decimals if there are none
    if ((unit === 'kWh') && (Math.abs(value) > (1000000/cut_off))) {
      formattedValue = (value / 1000000).toFixed(round_factor);
      return `${formattedValue} GWh`.trim();
    } else if ((unit === 'kWh') && (Math.abs(value) > (1000/cut_off))) {
      formattedValue = (value / 1000.0).toFixed(round_factor);
      return `${formattedValue} MWh`.trim();
    } else if ((unit === 'Wh') && (Math.abs(value) > (1000000/cut_off))) {
      formattedValue = (value / 1000000.0).toFixed(round_factor);
      return `${formattedValue} MWh`.trim();
    } else if ((unit === 'Wh') && (Math.abs(value) > (1000/cut_off))) {
      formattedValue = (value / 1000.0).toFixed(round_factor);
      return `${formattedValue} kWh`.trim();
    } else if ((unit === 'W') && (Math.abs(value) > (1000000/cut_off))) {
      formattedValue = (value / 1000000).toFixed(round_factor);
      return `${formattedValue} MW`.trim();
    } else if ((unit === 'W') && (Math.abs(value) > (1000/cut_off))) {
      formattedValue = (value / 1000).toFixed(round_factor);
      return `${formattedValue} kW`.trim();
    } else if ((unit === 'VA') && (Math.abs(value) > (1000/cut_off))) {
      formattedValue = (value / 1000).toFixed(round_factor);
      return `${formattedValue} kVA`.trim();
    } else if (value == Math.round(value)) {
      formattedValue = value;
    } else {
      formattedValue = value.toFixed(2);
    }
  }
  return `${formattedValue}${unit ? ' ' + unit : ''}`.trim();
};


export function mapModbusHoldingConfigByAddress(modbusConfig: ModbusConfig): Map<number, ModbusConfigItem> {
  const addressMap = new Map<number, ModbusConfigItem>();

  Object.entries(modbusConfig).forEach(([category, items]) => {

    // Update addressMap when modbusConfig registerType == holding only
    Object.entries(items).filter(([key, item]) => item.registerType === 'holding').forEach(([key, item]) => {
      addressMap.set(item.address, {
        ...item,
        category,
        dataKey: key,
      });
    });
  });

  return addressMap;
}

export function mapModbusConfigByAddress(modbusConfig: ModbusConfig): Map<number, ModbusConfigItem> {
  const addressMap = new Map<number, ModbusConfigItem>();

  Object.entries(modbusConfig).forEach(([category, items]) => {

    Object.entries(items).forEach(([key, item]) => {
      if (item.registerType == 'holding') {
      addressMap.set(item.address, {
        ...item,
        category,
        dataKey: key,
      });
    }
    });
  });

  return addressMap;
}

export function getHelpForAddress(addressMap: Map<number, ModbusConfigItem>, address: number): string {
  const item = addressMap.get(address);
  if (!item) {
    return `No help available for address ${address}`;
  }

  return `
Address: ${item.address} (0x${item.address.toString(16).padStart(4, '0')})
Name: ${item.name}
Category: ${item.category}
Unit: ${item.unit}
Data Type: ${item.dataType}
Register Type: ${item.registerType}
${item.description ? `Description: ${item.description}` : ''}
${item.scale ? `Scale: ${item.scale}` : ''}
${item.length ? `Length: ${item.length}` : ''}
  `.trim();
}

const imagePatterns: [RegExp, string][] = [
  // LG Energy Solution
  [/^LG(ES)?/i, '/inverters/LGES-5048.png'],

  // GoodWe
  [/GEH/i, '/inverters/GEH10-1U-1.png'],
  [/GW\d+K-BT/i, '/inverters/GoodWe-BT.png'],

  // Fronius
  [/Fro?nius/i, '/inverters/Fronuis-76.png'],
  [/Symo/i, '/inverters/Fronius-Symo.png'],
  [/Primo/i, '/inverters/Fronius-Primo.png'],

  // SolarEdge
  [/SE\d+/i, '/inverters/SE5000.png'],
  [/StorEdge/i, '/inverters/SolarEdge-StorEdge.png'],

  // Sungrow
  [/^ST.*CS-2H-AU/i, '/inverters/ST225.png'],
  [/^SH.*T/i, '/inverters/SH10RTV112.png'],
  [/^SH.*RS/i, '/inverters/SH50RS.png'],
  [/^SG.*D/i, '/inverters/SG5K-D.png'],
  [/^SG.*/i, '/inverters/Sungrow-KTL-M.jpeg'],
  [/^Logger1000/i, '/inverters/logger1000.png'],
  [/^iHomeManager/i, '/inverters/iHomeManager.jpeg'],

  // SigEnergy
  [/SIGENERGY/i, '/inverters/sigenstor.png'],
  [/SigenStor/i, '/inverters/sigenstor.png'],

  // Huawei
  [/SUN2000/i, '/inverters/Huawei-SUN2000.jpeg'],

  // SMA
  [/Sunny Boy/i, '/inverters/SMA-SunnyBoy.jpeg'],
  [/Sunny Tripower/i, '/inverters/SMA-SunnyTripower.jpeg'],

  // Enphase
  [/IQ\d+/i, '/inverters/Enphase-IQ.jpeg'],

  // istore
  [/IS-HYB/i, '/inverters/istore.png'],

  // ABB
  [/UNO-DM-TL-PLUS/i, '/inverters/ABB-UNO.jpeg'],

  // Delta
  [/RPI/i, '/inverters/Delta-RPI.jpeg'],

  // FoxESS
  [/KH10/i, '/inverters/FoxESS-KH.png'],
  [/KH9/i, '/inverters/FoxESS-KH.png'],
  [/H1-5.0-E/i, '/inverters/FoxESS-5kW-inverter.png'],
  [/F5000-G2/i, '/inverters/FoxESS-5kW-inverter.png'],

  // GoodWe
  [/GW/i, '/inverters/GoodWe-10K.jpeg'],
  [/gw.*/i, '/inverters/gw5000-sbp-20.jpeg'],

  // Growatt
  [/SPH\d+/i, '/inverters/Growatt-SPH.jpeg'],

  // Tesla
  [/Powerwall/i, '/inverters/Tesla-Powerwall.png'],

  // Victron Energy
  [/MultiPlus/i, '/inverters/Victron-MultiPlus.png'],

  // Schneider Electric
  [/Conext/i, '/inverters/Schneider-Conext.png'],

  // Outback Power
  [/Radian/i, '/inverters/Outback-Radian.png'],

  // SAJ-CI
  [/^CHS2.*/i, '/inverters/SAJ-CH2.png'],

  // SAJ
  [/^H2.*/i, '/inverters/SAJ-H2.png'],

  // Sonnen
  [/45710/i, '/inverters/sonnen.jpeg'],
  [/AU 9010 ND/i, '/inverters/sonnen.jpeg'],
  [/UK 9010 ND/i, '/inverters/sonnen.jpeg'],
];

export const getInverterImage = (product_name: string, manufacturer?: string | null): string => {
  if (manufacturer && /atmoce/i.test(manufacturer)) {
    return '/inverters/AtmoceBattery.png';
  }
  for (const [pattern, imagePath] of imagePatterns) {
    if (pattern.test(product_name)) {
      return imagePath;
    }
  }
  return '/inverters/default.png';
};

const sitesCache: Map<string, Promise<Response>> = new Map();

export async function cachedFetchSites(installerId?: string | null): Promise<Response> {
  const cacheKey = installerId ? `installer_${installerId}` : 'all';
  
  if (!sitesCache.has(cacheKey)) {
    const url = installerId 
      ? `/api/sites?installer_id=${encodeURIComponent(installerId)}`
      : '/api/sites';
    sitesCache.set(cacheKey, fetch(url));
  }
  
  return sitesCache.get(cacheKey)!;
}

let userCache: Promise<any> | null = null;

// Drop the cached /api/user result after a mutation (e.g. Discord unlink) so
// the next cachedFetchUser() call refetches instead of serving stale state.
export function invalidateUserCache(): void {
  userCache = null;
}

export async function cachedFetchUser(): Promise<any> {
  if (!userCache) {
    userCache = Promise.all([
      // Check res.ok before parsing: a 502/504 during a deploy returns an HTML
      // error page, and res.json() on that throws a cryptic
      // "Unexpected token '<'" instead of a meaningful error.
      fetch('/api/user').then(res => {
        if (!res.ok) throw new UserSessionRequestError(res.status);
        return res.json();
      }),
      // Coupons are non-essential — degrade to none rather than failing the
      // whole user load if that endpoint hiccups.
      fetch('/api/user_coupons').then(res => (res.ok ? res.json() : []))
    ]).then(([user, coupons]) => ({
      ...user,
      user_coupons: coupons
    })).catch((err) => {
      // Never cache a failed lookup. Otherwise a transient error (e.g. a 502
      // mid-deploy) sticks for the life of the page and, in production, bounces
      // a logged-in user to /login. Clearing the cache lets the next call retry.
      userCache = null;
      throw err;
    });
  }
  // Return a deep copy to keep the cached object immutable
  const user = await userCache;
  return JSON.parse(JSON.stringify(user));
}

export const getUserImage = (user: any): string => {
  if (user && user.image && user.image.trim() !== '') {
    if (user.image.startsWith('http')) {
      return user.image;
    }
    return `/api/logos/${user.image}`;
  }
  return '/images/profile.png';
}

export const spinner = (
  <>
  <div className="flex items-center justify-center">loading...
    <div className="w-8 h-8 border-4 border-blue-500 border-dotted rounded-full animate-spin"></div>
  </div>
  <div className="absolute top-0 left-0 w-full h-full bg-opacity-50"></div>
  </>
);

export const determineStyle = (action?: string | null) => {
  if (!action || typeof action !== 'string') {
    return "bg-muted0 text-white";
  }
  if (action.match(/import/i)) {
    return "bg-blue-500 text-white";
  } else if (action.match(/export/i)) {
    return "bg-green-500 text-white";
  } else if (action.match(/auto/i)) {
    return "bg-yellow-500 text-gray-800";
  } else if (action.match(/stopped/i)) {
    return "bg-red-500 text-white";
  } else if (action.match(/charge/i)) {
    return "bg-blue-500 text-white";
  } else if (action.match(/discharge/i)) {
    return "bg-green-500 text-white";
  }
  return "bg-muted0 text-white";
};

export const getEmoji = (key: string) => {
  const emojis: Record<string, string> = {
    "timezone_str": "🌍",          // Timezone information
    "current_hour": "🕐",          // Current hour of day
    "hours_until_midnight": "🌙",  // Hours until midnight
    "time_left": "⏰",            // Time remaining in period

    // Power & Energy Variables
    "solar_power": "☀️",           // Solar generation power
    "grid_power": "🌐",            // Grid power (import/export)
    "house_power": "🏠",           // House consumption
    "battery_soc": "🔋",           // Battery state of charge
    "optimal_charging": "c⚡",      // Optimal charging rate
    "optimal_discharging": "d⚡",   // Optimal discharging rate
    "import_soc": "📥",            // Import SOC threshold
    "feed_in_power_limitation": "🎛️",  // Power export limit
    "night_reserve": "🔒",         // Night reserve SOC
    "soc_surplus": "💪",           // Battery surplus available
    "projected_deficit": "💀",     // Projected energy deficit

    // Pricing Variables
    "buy_price": "🛒",             // Current buy price
    "sell_price": "💲",            // Current sell price
    "rrp": "💎",                   // Regional reference price
    "general_tariff": "💰",        // General electricity tariff
    "feed_in_tariff": "💸",        // Feed-in tariff rate
    "always_export_rrp": "🚀",     // Auto-export RRP threshold

    // Weather & Solar Variables
    "cloud_cover": "☁️",           // Cloud coverage percentage
    "gti_sum_tomorrow": "🌄",      // Tomorrow"s solar forecast
    "gti_today": "🌞",             // Today"s solar forecast
    "gti_past": "📅",              // Past solar generation
    "gti_to_2pm": "🕑",            // Solar forecast until 2PM
    "first_good_gti": "🌅",        // First good solar hour
    "last_good_gti": "🌇",         // Last good solar hour
    "is_solar_window_now": "🔆",   // Currently in solar window
    "is_daytime": "☀️",            // Daytime flag

    // System State Variables
    "last_action": "🔄",           // Previous system action
    "action_method": "⚙️",         // Method used for action

    // System Configuration
    "APP_VERSION": "📱",           // App version number
    "serial_number": "🏷️",        // Device serial number
    "manufacturer": "🏭",          // Device manufacturer
    "product_name": "📦",          // Product name
    "site_id": "🆔",              // Site identifier
    "state": "🗺️",                // State/region
    "inverter_id": "🔌",           // Inverter identifier
    "user_code_id": "👤",          // User code identifier
    "user_code_name": "📝",        // User code name

    // Calculated Pricing Variables
    'threshold': '🎯',
    'tolerant_price': '🎯💰',
    'low_buy_price': '🛒📉',
    'high_sell_price': '💲📈',
    'high_forecast': '📈🔮',
    'low_buy_forecast': '🛒🔮',
    'low_buy_adjusted': '🛒⚙️',
    'low_buy_near_term': '🛒⏰',
    'upcoming_min': '🛒⏳',
    'upcoming_negative': '📉❄️',
    'max_buy': '🛒🔝',
    'max_buy_forecast': '🛒🔝🔮',
    'best_upcoming': '🛒⭐',
    'best_upcoming_price': '🛒⭐💎',
    'cut_off': '✂️📊',
    'forecast_cutoff': '✂️🔮',
    'target_sell': '🎯💲',
    'current_sell': '💲🕐',
    'current_buy': '🛒🕐',
    
    // Price Analysis & Margins
    'margin': '💎',
    'price_margin': '💎📏',
    'margin_vs_low_buy': '💎🛒',
    'threshold_pct': '📊💯',
    'sell_vs_buy_ratio': '⚖️💲',
    'price_delta': '📊📐',
    'arbitrage_opportunity': '💎🔄',
    
    // Solar & Weather Calculations
    'gti_forecast': '🌤️🔮',
    'solar_progress': '🌄📊',
    'precent_pv_past': '📅☀️',
    
    // Battery Strategy Variables
    'current_soc': '🔋🕐',
    'soc': '🔋',
    'target_soc': '🎯🔋',
    'soc_vs_target': '🎯🔋📐',
    'min_soc': '🔋🔒',
    'battery_headroom': '🔋📏',
    
    // Time-Based Variables
    'time_left_hours': '⏱️⏰',
    
    // Strategy & Decision Context
    'priority': '📝🎖️',
    'windows_available': '🪟📊',
    'charge_fors': '⚡🔮',
    'power_limit': '🎛️⚡',
    'ev_charge_limit': '🚗⚡',
    'mqtt_topic_ocpp_max_charge': '🚗',
    'new_threshold': '🆕🎯',
    'power_limitation': '🎛️🔒',
    'feed_limitation': '🎛️📤',
    
    // Deficit & Surplus Analysis  
    'spike_count': '🎪🔢',
    'over_count': '🎪📊',
    'is_spike': '🔥✅',
    'max_spike': '🚀🔝',
    'max_spike_value': '🚀💎',
    
    // Forecast Analysis
    'windows_can_export': '🪟📤',
    'look_ahead': '🔮👀',
    'upcoming_6_hour_min': '🔮🕕',
    
    // Additional Context Variables
    'forecast_confidence': '🤖🎯',
    'prediction_accuracy': '🎯📈',
    'risk_level': '⚠️📊',
    'safety_margin': '🛡️📏',
    'efficiency_score': '📈⭐',
    'savings_potential': '💰🌟',
    'charge_rate': '⚡📊',
    'discharge_rate': '⚡📉',
    
    // Market Analysis
    'volatility': '📊🌊',
    'trend_direction': '📈🧭',
    'market_pressure': '💹🔥',
    'demand_forecast': '📊🏠',
    'supply_forecast': '📊🏭',
    
    // System Performance
    'response_time': '⚡⏱️',
    'accuracy_score': '🎯💯',
    'optimization_level': '⚙️📊',
    'learning_rate': '🧠📈',
    'confidence_interval': '🤖📊',
    
    // Risk Management
    'exposure_level': '⚠️📊',
    'hedge_ratio': '🛡️⚖️',
    'stop_loss': '🛑💰',
    'profit_target': '🎯💰',
    
    // Weather Impact
    'weather_confidence': '🌤️🎯',
    'cloud_impact': '☁️📊',
    'temperature_factor': '🌡️📊',
    'seasonal_adjustment': '🗓️⚙️',
    
    // Grid & Network
    'grid_stability': '🌐⚖️',
    'network_congestion': '🌐🚧',
    'export_capacity': '📤⚡',
    'import_capacity': '📥⚡',
    
    // User Behavior
    'consumption_pattern': '🏠📊',
    'usage_prediction': '🏠🔮',
    'behavior_score': '👤📊',
    'preference_weight': '👤⚖️'
  }
  return emojis[key] || key;

}

export function guessInverterName(site: Site): string {
  if (site.inverter_power && site.inverter_power == 10000) {
    return 'Sungrow SH10RS';
  } else if (site.inverter_power && site.inverter_power == 15000) {
    return 'Sungrow SH15RT';
  } else if (site.inverter_power && site.inverter_power == 25000) {
    return 'Sungrow SH25RT';
  } else if (site.inverter_power && site.inverter_power == 30000) {
    return '2 x Sungrow SH15RT';
  }
  return 'Sungrow SH10RS';
}
